import { useCallback, useMemo, useState } from 'react';

import type {
    ConstraintCatalogEntry,
    ConstraintInputValue,
    ConstraintLevelSelection,
    ConstraintSelection,
    Level,
} from '../../components/OptimizePlan/types';
import type { LevelValues } from '../queries/useConstraintOriginalValues';
import { constraintRowKey } from '../../utils/buildConstraintGroups';

// Never mutated: every update replaces the map wholesale.
const EMPTY_INPUTS = new Map<string, ConstraintInputValue>();

/**
 * Rewrites one KPI's level list and lets the deletion cascade: a KPI left with no
 * levels un-ticks itself rather than lingering as a header with nothing under it.
 *
 * Only the KPI being edited can be dropped — a KPI the user has just ticked starts
 * with no levels on purpose, waiting for its first one.
 */
function updateLevels(
    selections: ConstraintSelection[],
    constraintName: string,
    update: (levels: ConstraintLevelSelection[]) => ConstraintLevelSelection[],
): ConstraintSelection[] {
    return selections.flatMap((selection) => {
        if (selection.name !== constraintName) return [selection];
        const levels = update(selection.levels);
        return levels.length === 0 ? [] : [{ ...selection, levels }];
    });
}

interface UseOptimizeConstraintsParams {
    /**
     * The constraint list from the original-values response, already filtered by the
     * backend to what this objective can be constrained on. Switching the objective
     * refetches and swaps it, so a KPI the new objective does not offer drops out of
     * the selector — without being erased from `selections`, so switching back
     * restores it.
     */
    catalog: ConstraintCatalogEntry[];
    /**
     * Fetched attribute rows, needed here only to tell whether deleting a row was the
     * last one at that level.
     */
    byLevel: Map<Level, LevelValues>;
    /**
     * Changes to the objective, the start date or the scope invalidate every fetched
     * baseline. Typed values are dropped alongside them so no input outlives the
     * numbers it was derived from.
     */
    valuesContextSignature: string;
}

/**
 * Owns the only constraint state the modal keeps: which KPIs are selected, which
 * levels each carries, which attribute rows the user deleted, and which values they
 * typed. Everything rendered is derived from this by `buildConstraintGroups`.
 */
export function useOptimizeConstraints({ catalog, byLevel, valuesContextSignature }: UseOptimizeConstraintsParams) {
    const [selections, setSelections] = useState<ConstraintSelection[]>([]);
    const [inputs, setInputs] = useState(EMPTY_INPUTS);

    // Adjusting state during render rather than in an effect, so stale inputs are
    // never rendered for a frame against freshly fetched baselines.
    const [lastSignature, setLastSignature] = useState(valuesContextSignature);
    if (lastSignature !== valuesContextSignature) {
        setLastSignature(valuesContextSignature);
        setInputs(EMPTY_INPUTS);
    }

    const selectableKeys = useMemo(() => new Set(catalog.map((entry) => entry.key)), [catalog]);

    const activeSelections = useMemo(
        () => selections.filter((selection) => selectableKeys.has(selection.name)),
        [selections, selectableKeys],
    );

    const selectedConstraintNames = useMemo(
        () => activeSelections.map((selection) => selection.name),
        [activeSelections],
    );

    const kpiOptions = useMemo(
        () => catalog.map((entry) => ({ name: entry.key, label: entry.label })),
        [catalog],
    );

    const onKPISelectHandler = useCallback((updatedNames: string[]) => {
        const selected = new Set(updatedNames);
        setSelections((prev) => {
            // Existing entries keep their levels and deletions; only newly ticked KPIs are appended.
            const kept = prev.filter((selection) => selected.has(selection.name));
            const keptNames = new Set(kept.map((selection) => selection.name));
            const added = updatedNames
                .filter((name) => !keptNames.has(name))
                .map((name) => ({ name, levels: [] }));
            return [...kept, ...added];
        });
    }, []);

    const onAddLevel = useCallback((constraintName: string, level: Level) => {
        setSelections((prev) =>
            prev.map((selection) =>
                selection.name === constraintName && !selection.levels.some((l) => l.level === level)
                    ? { ...selection, levels: [...selection.levels, { level, removedAttributes: [] }] }
                    : selection,
            ),
        );
    }, []);

    const onRemoveLevel = useCallback((constraintName: string, level: Level) => {
        setSelections((prev) =>
            updateLevels(prev, constraintName, (levels) => levels.filter((l) => l.level !== level)),
        );
    }, []);

    const onRemoveAttribute = useCallback(
        (constraintName: string, level: Level, attributeName: string) => {
            setSelections((prev) =>
                updateLevels(prev, constraintName, (levels) =>
                    levels.flatMap((l) => {
                        if (l.level !== level) return [l];

                        const removedAttributes = [...l.removedAttributes, attributeName];
                        const removed = new Set(removedAttributes);
                        const remaining = (byLevel.get(level)?.get(constraintName) ?? []).filter(
                            (attribute) => !removed.has(attribute.attribute_name),
                        );

                        // Deleting the last row takes the level with it, rather than
                        // leaving a header over an empty block.
                        return remaining.length === 0 ? [] : [{ ...l, removedAttributes }];
                    }),
                ),
            );
        },
        [byLevel],
    );

    const onInputChange = useCallback(
        (
            constraintName: string,
            level: Level,
            attributeName: string,
            patch: Partial<ConstraintInputValue>,
            fallback: ConstraintInputValue,
        ) => {
            const key = constraintRowKey(constraintName, level, attributeName);
            setInputs((prev) => {
                const next = new Map(prev);
                next.set(key, { ...(prev.get(key) ?? fallback), ...patch });
                return next;
            });
        },
        [],
    );

    const resetConstraints = useCallback(() => {
        setSelections([]);
        setInputs(EMPTY_INPUTS);
    }, []);

    return {
        selections: activeSelections,
        inputs,
        kpiOptions,
        selectedConstraintNames,
        onKPISelectHandler,
        onAddLevel,
        onRemoveLevel,
        onRemoveAttribute,
        onInputChange,
        resetConstraints,
    };
}
